/**
 * AI assistant tools for IT Ticketing (Phase 4 of the full support-ticketing
 * port). Registered into the shared AI panel's tool registry
 * (web/src/services/aiService.ts's AiTool shape), following CRM's own
 * crmAiTools pattern exactly: dynamic-imports this module's service layer so
 * the tool set costs nothing when the AI panel is closed, and every write
 * sets requiresConfirmation so the user approves it before anything happens.
 *
 * Wiring (done in aiService.ts, not here): import { itTicketingAiTools } and
 * push it into getPpmTools() behind isModuleVisible('it-ticketing').
 */
import type { AiTool } from '@/services/aiService'
import { addAuditEntry } from '@/services/aiService'
import { DEFAULT_TICKET_CATEGORIES, normalizeTicketCategory, type Ticket, type TicketStatus } from '../types/ticket'
import {
  QUERY_FIELDS, QUERY_OPS, SORT_FIELDS, compileTicketQuery, parseTicketQuery, runTicketQuery,
  type QueryContext, type QueryField, type TicketQuery,
} from '../lib/ticketQuery'
import type { BulkPatch } from '../lib/bulkTicketUpdate'

// Active category names from the admin-managed list (00574), or the built-in
// fallback if that table can't be read.
async function activeCategoryNames(): Promise<string[]> {
  try {
    const { listTicketCategories } = await import('./ticketingService')
    const names = (await listTicketCategories()).map((c) => c.name)
    return names.length > 0 ? names : [...DEFAULT_TICKET_CATEGORIES]
  } catch {
    return [...DEFAULT_TICKET_CATEGORIES]
  }
}

interface ToolUser { id: string; name: string }

async function currentActor(): Promise<{ id: string; name: string }> {
  try {
    const { useAppStore } = await import('@/store/appStore')
    const u = useAppStore.getState().currentUser as { id?: string; name?: string } | null | undefined
    if (u?.id) return { id: u.id, name: u.name ?? u.id }
  } catch { /* store unavailable outside the UI */ }
  return { id: 'ai', name: 'XPM Agent' }
}

function resolveUser(nameOrId: string | undefined, users: ToolUser[]): ToolUser | null {
  if (!nameOrId) return null
  const q = nameOrId.trim().toLowerCase()
  if (!q) return null
  return users.find((u) => u.id === nameOrId) ?? users.find((u) => u.name.toLowerCase() === q)
    ?? users.find((u) => u.name.toLowerCase().includes(q)) ?? null
}

function resolveTicket(idOrNumber: string | undefined, tickets: Ticket[]): Ticket | null {
  if (!idOrNumber) return null
  const q = idOrNumber.trim().toLowerCase()
  return tickets.find((t) => t.id === idOrNumber) ?? tickets.find((t) => t.ticketNumber.toLowerCase() === q) ?? null
}

function auditTicketingTool(action: string, entityId: string, details: Record<string, unknown>): void {
  addAuditEntry({ userId: 'ai', action, entityType: 'it_ticket', entityId, details, aiGenerated: true, confirmed: true })
}

// --- Plain-language ticket queries (lib/ticketQuery.ts) ---------------------
// One spec for list_tickets, show_tickets_in_grid and bulk_update_tickets, so
// "overdue Facilities tickets" means the same tickets in chat, on the grid,
// and in a bulk change.

const WHERE_PARAM = {
  type: 'array',
  description: [
    'Which tickets: conditions, all ANDed; an array value inside one condition is ORed. Each condition is {field, op, value}:',
    '- status: op in | not; a status, or "open" (anything not Resolved/Closed) or "closed" (Resolved or Closed)',
    '- priority, category: op in | not',
    '- customer: op in | not; the customer name',
    '- assignee: op in | not; a person (name or email), "me", or "unassigned"',
    '- requester (who it is created for), createdBy (who entered it): op in | not; a person or "me"',
    '- overdue: op is; "true" or "false". Overdue = open with a due date before today. Use this rather than rebuilding it from status and dueDate.',
    '- dueDate, createdAt, updatedAt: op before | after; YYYY-MM-DD. Resolve "last week", "this month" and the like to dates yourself from today’s date.',
    '- text: op contains | not; matched against ticket number, title and description',
    '- ticketNumber: op in | not; ticket numbers',
    'Leave it empty to mean every ticket the user can see.',
  ].join('\n'),
  items: {
    type: 'object',
    properties: {
      field: { type: 'string', enum: [...QUERY_FIELDS] },
      op: { type: 'string', enum: [...QUERY_OPS] },
      value: { description: 'A string, or an array of strings (ORed)' },
    },
    required: ['field', 'op', 'value'],
  },
}

const SORT_PARAM = {
  type: 'object',
  description: 'Optional sort. Blanks (no due date, unassigned) always go last.',
  properties: {
    field: { type: 'string', enum: [...SORT_FIELDS] },
    direction: { type: 'string', enum: ['asc', 'desc'] },
  },
}

async function loadQueryData() {
  const { listTickets, listTicketingCustomers, listTicketCategories } = await import('./ticketingService')
  const { getUsers } = await import('@/services/dataService')
  const { localTodayIso } = await import('../lib/ticketTasks')
  const [tickets, users, customers, categories, actor] = await Promise.all([
    listTickets(),
    getUsers(),
    listTicketingCustomers().catch(() => []),
    listTicketCategories({ includeInactive: true }).catch(() => []),
    currentActor(),
  ])
  const ctx: QueryContext = {
    users,
    customers,
    categories: [...new Set([...DEFAULT_TICKET_CATEGORIES, ...categories.map((c) => c.name), ...tickets.map((t) => t.category)])].filter(Boolean),
    currentUserId: actor.id === 'ai' ? null : actor.id,
    today: localTodayIso(),
  }
  return { tickets, users, ctx }
}

// Reads `where` / `sort` from tool input, folding in a tool's older flat
// filter params (whereStatus, assigneeName, ...) so existing prompts keep
// working. `legacy` maps an input key to the spec field it means.
function queryFromInput(input: Record<string, unknown>, legacy: Record<string, QueryField>): TicketQuery | { error: string } {
  const parsed = input.where === undefined && input.sort === undefined
    ? { where: [] as TicketQuery['where'] }
    : parseTicketQuery({ where: input.where ?? [], sort: input.sort })
  if (!parsed) return { error: 'The where/sort conditions aren’t in the expected shape: where is a list of {field, op, value}.' }
  for (const [key, field] of Object.entries(legacy)) {
    const v = input[key]
    if (typeof v === 'string' && v.trim()) parsed.where.push({ field, op: 'in', value: v.trim() })
  }
  return parsed
}

function ticketLine(t: Ticket, userName: (id: string | null) => string): string {
  return `${t.ticketNumber}: ${t.title} (${t.status}, ${t.priority}, ${userName(t.assigneeId)})`
}

type BulkPlan = {
  targets: Ticket[]
  patch: BulkPatch
  matched: string
  userName: (id: string | null) => string
}

// Which tickets and what change, for bulk_update_tickets. Shared by its
// preview (the Confirm card) and its handler, so what the user approved is
// what runs (re-read at confirm time, so a ticket that changed in between is
// judged on its current values).
async function planBulkUpdate(input: Record<string, unknown>): Promise<BulkPlan | { error: string }> {
  const { TICKET_STATUSES, TICKET_PRIORITIES } = await import('../types/ticket')
  const str = (v: unknown) => (typeof v === 'string' ? v.trim() : '')
  const pickEnum = <T extends string>(v: string, values: readonly T[]) => values.find((x) => x.replace(/\s/g, '').toLowerCase() === v.replace(/\s/g, '').toLowerCase())

  const query = queryFromInput(input, { whereStatus: 'status', wherePriority: 'priority', whereCategory: 'category', whereAssigneeName: 'assignee' })
  if ('error' in query) return query
  const { tickets, users, ctx } = await loadQueryData()
  const userName = (id: string | null) => (id ? users.find((u) => u.id === id)?.name ?? 'Unknown' : 'Unassigned')

  const numbers = str(input.ticketNumbers).split(',').map((s) => s.trim()).filter(Boolean)
  if (numbers.length > 0) {
    const missing = numbers.filter((n) => !resolveTicket(n, tickets))
    if (missing.length > 0) return { error: `No ticket matches: ${missing.join(', ')}.` }
    query.where.push({ field: 'ticketNumber', op: 'in', value: numbers.map((n) => resolveTicket(n, tickets)!.ticketNumber) })
  }
  // A bulk change always names its tickets: an empty selection would mean
  // "every ticket you can see", which is never what someone asked for here.
  if (query.where.length === 0) return { error: 'Say which tickets: give at least one condition or list ticket numbers.' }
  const compiled = compileTicketQuery({ where: query.where }, ctx)
  if (!compiled.ok) return { error: compiled.error }
  const targets = runTicketQuery(tickets, compiled)
  if (targets.length === 0) return { error: `No tickets are ${compiled.description}.` }

  const patch: BulkPatch = {}
  if (str(input.setStatus)) {
    const s = pickEnum(str(input.setStatus), TICKET_STATUSES)
    if (!s) return { error: `Unknown status "${str(input.setStatus)}".` }
    patch.status = s
  }
  if (str(input.setPriority)) {
    const p = pickEnum(str(input.setPriority), TICKET_PRIORITIES)
    if (!p) return { error: `Unknown priority "${str(input.setPriority)}".` }
    patch.priority = p
  }
  if (str(input.setCategory)) {
    const c = normalizeTicketCategory(str(input.setCategory), await activeCategoryNames())
    if (!c) return { error: `Unknown category "${str(input.setCategory)}". Use list_ticket_categories for the valid ones.` }
    patch.category = c
  }
  if (str(input.setAssigneeName)) {
    if (str(input.setAssigneeName).toLowerCase() === 'unassigned') patch.assigneeId = null
    else {
      const u = resolveUser(str(input.setAssigneeName), users)
      if (!u) return { error: `No user matches "${str(input.setAssigneeName)}".` }
      patch.assigneeId = u.id
    }
  }
  if (str(input.setDueDate)) {
    if (!/^\d{4}-\d{2}-\d{2}$/.test(str(input.setDueDate))) return { error: 'Give the due date as YYYY-MM-DD.' }
    patch.dueDate = str(input.setDueDate)
  }
  if (Object.keys(patch).length === 0) return { error: 'Say what to change (status, priority, category, assignee or due date).' }

  return { targets, patch, matched: compiled.description, userName }
}

export const itTicketingAiTools: AiTool[] = [
  {
    name: 'list_tickets',
    description: 'Find tickets and answer in chat: any filter (status incl. "open", priority, category, customer, assignee, created for, entered by, overdue, due/created/updated dates, text) and an optional sort. Read-only. To change what the Tickets grid shows instead, use show_tickets_in_grid.',
    parameters: {
      where: WHERE_PARAM,
      sort: SORT_PARAM,
      status: { type: 'string', description: 'Shorthand for a status condition' },
      priority: { type: 'string', description: 'Shorthand for a priority condition' },
      category: { type: 'string', description: 'Shorthand for a category condition' },
      assigneeName: { type: 'string', description: 'Shorthand for an assignee condition' },
      limit: { type: 'number', description: 'Max rows to return (default 20)' },
    },
    requiresConfirmation: false,
    handler: async (input) => {
      const query = queryFromInput(input, { status: 'status', priority: 'priority', category: 'category', assigneeName: 'assignee' })
      if ('error' in query) return { error: query.error }
      const { tickets, users, ctx } = await loadQueryData()
      const compiled = compileTicketQuery(query, ctx)
      if (!compiled.ok) return { error: compiled.error }
      const rows = runTicketQuery(tickets, compiled)
      const limit = typeof input.limit === 'number' ? input.limit : 20
      const userName = (id: string | null) => (id ? users.find((u) => u.id === id)?.name ?? 'Unknown' : 'Unassigned')
      return {
        matched: compiled.description,
        total: rows.length,
        tickets: rows.slice(0, limit).map((t) => ({
          ticketNumber: t.ticketNumber, title: t.title, status: t.status, priority: t.priority,
          category: t.category, assignee: userName(t.assigneeId), dueDate: t.dueDate,
        })),
      }
    },
  },
  {
    name: 'show_tickets_in_grid',
    description: 'Filter and/or sort the Tickets grid the user is looking at (the same where/sort as list_tickets), e.g. "show my critical tickets by due date", "only overdue Gentex tickets". Changes the view only, never any ticket, so it needs no confirmation. The grid shows a chip with what was applied and a Clear button. Use list_tickets when they just want an answer in chat.',
    parameters: {
      where: WHERE_PARAM,
      sort: SORT_PARAM,
    },
    requiresConfirmation: false,
    handler: async (input) => {
      const query = queryFromInput(input, {})
      if ('error' in query) return { error: query.error }
      const { tickets, ctx } = await loadQueryData()
      const compiled = compileTicketQuery(query, ctx)
      if (!compiled.ok) return { error: compiled.error }
      const total = tickets.filter(compiled.match).length
      const { useTicketGridCommand, ticketGridHref } = await import('../lib/ticketGridCommand')
      const { isItTicketingPath } = await import('../lib/itTicketingRoutes')
      // On an IT Ticketing page the route provider moves the grid right away;
      // anywhere else, hand back a link instead.
      const onTicketing = typeof window !== 'undefined' && isItTicketingPath(window.location.pathname)
      if (onTicketing) useTicketGridCommand.getState().showInGrid(query)
      return {
        ok: true,
        applied: onTicketing,
        showing: compiled.description,
        total,
        link: ticketGridHref(query),
        message: onTicketing
          ? `The Tickets grid now shows ${compiled.description} (${total} ticket${total === 1 ? '' : 's'}).`
          : `Open the Tickets grid with this view: ${ticketGridHref(query)}`,
      }
    },
  },
  {
    name: 'summarize_tickets',
    description: 'Summarize the open ticket queue: counts by status and by assignee. Read-only.',
    parameters: {},
    requiresConfirmation: false,
    handler: async () => {
      const { listTickets } = await import('./ticketingService')
      const { getUsers } = await import('@/services/dataService')
      const [tickets, users] = await Promise.all([listTickets(), getUsers()])
      const userName = (id: string | null) => (id ? users.find((u) => u.id === id)?.name ?? 'Unknown' : 'Unassigned')
      const byStatus = new Map<string, number>()
      const byAssignee = new Map<string, number>()
      for (const t of tickets) {
        byStatus.set(t.status, (byStatus.get(t.status) ?? 0) + 1)
        const who = userName(t.assigneeId)
        byAssignee.set(who, (byAssignee.get(who) ?? 0) + 1)
      }
      return {
        total: tickets.length,
        byStatus: Object.fromEntries(byStatus),
        byAssignee: Object.fromEntries(byAssignee),
      }
    },
  },
  {
    name: 'create_ticket',
    description: 'File a new IT support ticket. It is created by the signed-in user, and created for them unless requesterName names someone else (filing on that person’s behalf, which agents and admins can do). Writes on confirmation.',
    parameters: {
      title: { type: 'string', description: 'Short ticket title' },
      description: { type: 'string', description: 'Full description of the issue or request' },
      category: { type: 'string', description: 'Required. An active category name (see list_ticket_categories)' },
      priority: { type: 'string', description: 'Low | Medium | High | Critical. Prefer the assessment answers below; set this only to override the assessed priority (then also give overrideReason).' },
      production: { type: 'string', description: 'Assessment: impact to production. down | degraded | minor | none' },
      usersAffected: { type: 'string', description: 'Assessment: who is affected. organization | department | few | one' },
      workaround: { type: 'string', description: 'Assessment: can work continue another way? none (blocked) | painful | easy' },
      deadline: { type: 'string', description: 'Assessment: when it must be fixed to avoid business harm. today | week | later' },
      securityConcern: { type: 'string', description: 'Assessment: security or data integrity concern? confirmed | possible | no' },
      overrideReason: { type: 'string', description: 'Required when priority is given and differs from the assessed priority' },
      assigneeName: { type: 'string', description: 'Optional — the person to assign it to (fuzzy-matched)' },
      requesterName: { type: 'string', description: 'Optional: who the ticket is created for (fuzzy-matched). Defaults to the signed-in user.' },
    },
    requiresConfirmation: true,
    handler: async (input) => {
      const title = typeof input.title === 'string' ? input.title.trim() : ''
      const description = typeof input.description === 'string' ? input.description.trim() : ''
      const categoryRaw = typeof input.category === 'string' ? input.category.trim() : ''
      const categoryNames = await activeCategoryNames()
      const category = normalizeTicketCategory(categoryRaw, categoryNames)
      if (!title || !description || !categoryRaw) return { error: 'A ticket needs a title, description, and category.' }
      if (!category) return { error: `"${categoryRaw}" is not a ticket category. Use one of: ${categoryNames.join(', ')}.` }
      const { createTicket } = await import('./ticketingService')
      const { notifyTicketCreated } = await import('../lib/teamsNotifications')
      const { getUsers } = await import('@/services/dataService')
      const users = await getUsers()
      const actor = await currentActor()
      let requesterId = actor.id
      if (typeof input.requesterName === 'string' && input.requesterName) {
        const u = resolveUser(input.requesterName, users)
        if (!u) return { error: `No user matches "${input.requesterName}".` }
        requesterId = u.id
      }
      let assigneeId: string | null = null
      if (typeof input.assigneeName === 'string' && input.assigneeName) {
        const u = resolveUser(input.assigneeName, users)
        if (!u) return { error: `No user matches "${input.assigneeName}".` }
        assigneeId = u.id
      }
      // Priority: from the assessment answers when all five are given (same
      // scoring as the New ticket form); an explicit priority that differs
      // from the assessment is an override and needs a reason.
      const { buildAssessment, parseAssessment } = await import('../lib/priorityAssessment')
      const str = (v: unknown) => (typeof v === 'string' ? v.trim().toLowerCase() : '')
      const candidate = parseAssessment({
        version: 1,
        answers: {
          production: str(input.production), users: str(input.usersAffected), workaround: str(input.workaround),
          deadline: str(input.deadline), security: str(input.securityConcern),
        },
      })
      const explicit = (['Low', 'Medium', 'High', 'Critical'] as const).find((p) => p.toLowerCase() === str(input.priority))
      const overrideReason = typeof input.overrideReason === 'string' ? input.overrideReason.trim() : ''
      if (candidate && explicit && explicit !== candidate.suggested && !overrideReason) {
        return { error: `The assessment gives ${candidate.suggested}. To file it as ${explicit} instead, give an overrideReason.` }
      }
      const assessment = candidate ? buildAssessment(candidate.answers, explicit && explicit !== candidate.suggested ? overrideReason : undefined) : null
      const priority = explicit ?? candidate?.suggested ?? 'Medium'
      try {
        const ticket = await createTicket({
          title, description, category,
          priority,
          priorityAssessment: assessment,
          requesterId, assigneeId,
        })
        auditTicketingTool('create_ticket', ticket.id, { ticketNumber: ticket.ticketNumber, title })
        const requesterName = users.find((u) => u.id === requesterId)?.name ?? actor.name
        notifyTicketCreated(ticket, requesterName, actor.name).catch(() => {})
        const onBehalf = requesterId !== actor.id ? ` on behalf of ${requesterName}` : ''
        return { ok: true, ticketNumber: ticket.ticketNumber, message: `Created ${ticket.ticketNumber}: ${ticket.title}${onBehalf}.` }
      } catch (e) {
        return { error: e instanceof Error ? e.message : String(e) }
      }
    },
  },
  {
    name: 'update_ticket_status',
    description: 'Change a ticket\'s status. Identify the ticket by its number (e.g. TICK-00042) or id. Writes on confirmation.',
    parameters: {
      ticketNumber: { type: 'string', description: 'The ticket number or id' },
      status: { type: 'string', description: 'New | Assigned | InProgress | OnHold | Deferred | Resolved | Closed' },
    },
    requiresConfirmation: true,
    handler: async (input) => {
      const status = typeof input.status === 'string' ? (input.status as TicketStatus) : null
      if (!status) return { error: 'Name the new status.' }
      const { listTickets, updateTicket } = await import('./ticketingService')
      const tickets = await listTickets()
      const ticket = resolveTicket(input.ticketNumber as string | undefined, tickets)
      if (!ticket) return { error: `No ticket matches "${String(input.ticketNumber ?? '')}".` }
      try {
        const updated = await updateTicket(ticket.id, { status })
        auditTicketingTool('update_ticket_status', ticket.id, { from: ticket.status, to: status })
        return { ok: true, ticketNumber: updated.ticketNumber, message: `${updated.ticketNumber} is now ${updated.status}.` }
      } catch (e) {
        return { error: e instanceof Error ? e.message : String(e) }
      }
    },
  },
  {
    name: 'add_ticket_comment',
    description: 'Post a status-update comment on a ticket. Identify the ticket by its number (e.g. TICK-00042) or id. Writes on confirmation.',
    parameters: {
      ticketNumber: { type: 'string', description: 'The ticket number or id' },
      body: { type: 'string', description: 'The comment text' },
    },
    requiresConfirmation: true,
    handler: async (input) => {
      const body = typeof input.body === 'string' ? input.body.trim() : ''
      if (!body) return { error: 'The comment needs text.' }
      const { listTickets, addComment } = await import('./ticketingService')
      const tickets = await listTickets()
      const ticket = resolveTicket(input.ticketNumber as string | undefined, tickets)
      if (!ticket) return { error: `No ticket matches "${String(input.ticketNumber ?? '')}".` }
      const actor = await currentActor()
      try {
        const comment = await addComment({ ticketId: ticket.id, authorId: actor.id, body })
        auditTicketingTool('add_ticket_comment', ticket.id, { commentId: comment.id })
        return { ok: true, ticketNumber: ticket.ticketNumber, message: `Posted a comment on ${ticket.ticketNumber}.` }
      } catch (e) {
        return { error: e instanceof Error ? e.message : String(e) }
      }
    },
  },
  {
    name: 'get_ticket',
    description: 'Full detail for one ticket: description, requester, assignee, dates, tasks, recent comments, and how long it has been in its current status and with its current assignee. Identify it by number (e.g. TICK-00042) or id. Read-only.',
    parameters: {
      ticketNumber: { type: 'string', description: 'The ticket number or id' },
      commentLimit: { type: 'number', description: 'How many of the most recent comments to include (default 5)' },
    },
    requiresConfirmation: false,
    handler: async (input) => {
      const { getTicket, listTickets, listComments, listTicketStateHistory, listTicketTasks } = await import('./ticketingService')
      const { statusSpans, queueSpans, totalByValue, formatDuration, QUEUE_UNASSIGNED } = await import('../lib/ticketTimeTracking')
      const { getUsers } = await import('@/services/dataService')
      const ref = typeof input.ticketNumber === 'string' ? input.ticketNumber.trim() : ''
      if (!ref) return { error: 'Name the ticket number or id.' }
      // Direct id lookup first, so a ticket outside the list's current scope
      // (e.g. opened from a link) still resolves; then fall back to the number.
      const ticket = (await getTicket(ref)) ?? resolveTicket(ref, await listTickets())
      if (!ticket) return { error: `No ticket matches "${ref}".` }
      const [users, comments, history, tasks] = await Promise.all([getUsers(), listComments(ticket.id), listTicketStateHistory(ticket.id), listTicketTasks(ticket.id)])
      const userName = (id: string | null) => (id ? users.find((u) => u.id === id)?.name ?? 'Unknown' : 'Unassigned')
      const endedAt = ticket.resolvedAt ? new Date(ticket.resolvedAt) : new Date()
      const sSpans = statusSpans(history, endedAt)
      const assigneeMs = totalByValue(queueSpans(history, endedAt)).get(ticket.assigneeId ?? QUEUE_UNASSIGNED) ?? 0
      const commentLimit = typeof input.commentLimit === 'number' ? input.commentLimit : 5
      return {
        ticketNumber: ticket.ticketNumber,
        title: ticket.title,
        description: ticket.description,
        status: ticket.status,
        priority: ticket.priority,
        priorityAssessment: ticket.priorityAssessment
          ? { suggested: ticket.priorityAssessment.suggested, impact: ticket.priorityAssessment.impact, urgency: ticket.priorityAssessment.urgency, answers: ticket.priorityAssessment.answers, overrideReason: ticket.priorityAssessment.overrideReason ?? null }
          : null,
        category: ticket.category,
        createdFor: userName(ticket.requesterId),
        createdBy: ticket.createdBy ? userName(ticket.createdBy) : 'not recorded',
        assignee: userName(ticket.assigneeId),
        createdAt: ticket.createdAt,
        dueDate: ticket.dueDate,
        resolvedAt: ticket.resolvedAt,
        timeInCurrentStatus: formatDuration(sSpans[sSpans.length - 1]?.durationMs ?? 0),
        timeWithCurrentAssignee: formatDuration(assigneeMs),
        commentCount: comments.length,
        recentComments: comments.slice(-commentLimit).map((c) => ({ author: userName(c.authorId), at: c.createdAt, body: c.body })),
        tasks: tasks.map((t) => ({ id: t.id, title: t.title, done: t.done, assignee: t.assigneeId ? userName(t.assigneeId) : null, dueDate: t.dueDate })),
      }
    },
  },
  {
    name: 'assign_ticket',
    description: 'Assign (or reassign) a ticket to a person, or unassign it. Identify the ticket by number (e.g. TICK-00042) or id. Writes on confirmation.',
    parameters: {
      ticketNumber: { type: 'string', description: 'The ticket number or id' },
      assigneeName: { type: 'string', description: 'The person to assign it to (fuzzy-matched), or "unassigned" to clear it' },
    },
    requiresConfirmation: true,
    handler: async (input) => {
      const who = typeof input.assigneeName === 'string' ? input.assigneeName.trim() : ''
      if (!who) return { error: 'Name who to assign it to, or "unassigned".' }
      const { getTicket, listTickets, updateTicket } = await import('./ticketingService')
      const { getUsers } = await import('@/services/dataService')
      const ref = typeof input.ticketNumber === 'string' ? input.ticketNumber.trim() : ''
      const ticket = (ref ? await getTicket(ref) : null) ?? resolveTicket(ref, await listTickets())
      if (!ticket) return { error: `No ticket matches "${ref}".` }
      let assigneeId: string | null = null
      if (who.toLowerCase() !== 'unassigned') {
        const u = resolveUser(who, await getUsers())
        if (!u) return { error: `No user matches "${who}".` }
        assigneeId = u.id
      }
      try {
        const updated = await updateTicket(ticket.id, { assigneeId })
        auditTicketingTool('assign_ticket', ticket.id, { from: ticket.assigneeId, to: assigneeId })
        return { ok: true, ticketNumber: updated.ticketNumber, message: assigneeId ? `${updated.ticketNumber} is now assigned to ${who}.` : `${updated.ticketNumber} is now unassigned.` }
      } catch (e) {
        return { error: e instanceof Error ? e.message : String(e) }
      }
    },
  },
  {
    name: 'find_stale_tickets',
    description: 'Find open tickets that have sat in their current status the longest (oldest first), optionally only for one assignee or above a minimum age. Read-only.',
    parameters: {
      minHours: { type: 'number', description: 'Only tickets in their current status at least this many hours (default 0)' },
      assigneeName: { type: 'string', description: "Optional: only this assignee's tickets (fuzzy-matched), or \"unassigned\"" },
      limit: { type: 'number', description: 'Max rows to return (default 10)' },
    },
    requiresConfirmation: false,
    handler: async (input) => {
      const { listTickets, listAllStateHistory } = await import('./ticketingService')
      const { statusSpans, formatDuration } = await import('../lib/ticketTimeTracking')
      const { getUsers } = await import('@/services/dataService')
      const [tickets, users] = await Promise.all([listTickets(), getUsers()])
      let open = tickets.filter((t) => t.status !== 'Resolved' && t.status !== 'Closed')
      const who = typeof input.assigneeName === 'string' ? input.assigneeName.trim() : ''
      if (who) {
        if (who.toLowerCase() === 'unassigned') open = open.filter((t) => !t.assigneeId)
        else {
          const u = resolveUser(who, users)
          if (!u) return { error: `No user matches "${who}".` }
          open = open.filter((t) => t.assigneeId === u.id)
        }
      }
      const history = open.length > 0 ? await listAllStateHistory(open.map((t) => t.id)) : []
      const now = new Date()
      const userName = (id: string | null) => (id ? users.find((u) => u.id === id)?.name ?? 'Unknown' : 'Unassigned')
      const minMs = (typeof input.minHours === 'number' ? input.minHours : 0) * 3_600_000
      const rows = open
        .map((t) => {
          const spans = statusSpans(history.filter((e) => e.ticketId === t.id), now)
          return { t, ms: spans[spans.length - 1]?.durationMs ?? now.getTime() - new Date(t.createdAt).getTime() }
        })
        .filter((r) => r.ms >= minMs)
        .sort((a, b) => b.ms - a.ms)
      const limit = typeof input.limit === 'number' ? input.limit : 10
      return {
        total: rows.length,
        tickets: rows.slice(0, limit).map(({ t, ms }) => ({
          ticketNumber: t.ticketNumber, title: t.title, status: t.status, priority: t.priority,
          assignee: userName(t.assigneeId), timeInCurrentStatus: formatDuration(ms),
        })),
      }
    },
  },
  {
    name: 'queue_time_report',
    description: 'How long tickets have sat in queues, from ticket history: grouped by user, organization (the customer a ticket was under at the time) or status, with time per status. Optionally filter by organization name, status or user name. "In queue" excludes Deferred, Resolved and Closed time. Read-only.',
    parameters: {
      groupBy: { type: 'string', description: 'user | organization | status (default user)' },
      organization: { type: 'string', description: 'Optional: only this customer (name, fuzzy-matched)' },
      status: { type: 'string', description: 'Optional: only time spent in this status' },
      userName: { type: 'string', description: 'Optional: only this person’s queue (fuzzy-matched), or "unassigned"' },
      limit: { type: 'number', description: 'Max rows (default 15)' },
    },
    requiresConfirmation: false,
    handler: async (input) => {
      const { listTickets, listAllStateHistory, listTicketingCustomers } = await import('./ticketingService')
      const { historySegments, pivotHistory, formatDuration, QUEUE_UNASSIGNED, UNASSIGNED_ORG } = await import('../lib/ticketTimeTracking')
      const { getUsers } = await import('@/services/dataService')
      const [tickets, users, customers] = await Promise.all([listTickets(), getUsers(), listTicketingCustomers()])
      const groupBy = input.groupBy === 'organization' || input.groupBy === 'status' ? input.groupBy : 'user'

      let orgId: string | null = null
      if (typeof input.organization === 'string' && input.organization.trim()) {
        const q = input.organization.trim().toLowerCase()
        const c = customers.find((x) => x.name.toLowerCase() === q) ?? customers.find((x) => x.name.toLowerCase().includes(q))
        if (!c) return { error: `No customer matches "${input.organization}". Known: ${customers.map((x) => x.name).join(', ')}.` }
        orgId = c.id
      }
      let assigneeId: string | null = null
      if (typeof input.userName === 'string' && input.userName.trim()) {
        if (input.userName.trim().toLowerCase() === 'unassigned') assigneeId = QUEUE_UNASSIGNED
        else {
          const u = resolveUser(input.userName, users)
          if (!u) return { error: `No user matches "${input.userName}".` }
          assigneeId = u.id
        }
      }
      const status = typeof input.status === 'string' && input.status.trim() ? input.status.trim() : null

      const history = tickets.length > 0 ? await listAllStateHistory(tickets.map((t) => t.id)) : []
      const now = new Date()
      const segments = tickets.flatMap((t) => historySegments(
        history.filter((e) => e.ticketId === t.id),
        t.resolvedAt ? new Date(t.resolvedAt) : now,
        { open: !t.resolvedAt },
      ))
      const rows = pivotHistory(segments, groupBy, { orgId, status, assigneeId })
      const label = (key: string) =>
        groupBy === 'status' ? key
        : groupBy === 'organization' ? (key === UNASSIGNED_ORG ? 'No customer' : customers.find((c) => c.id === key)?.name ?? key)
        : key === QUEUE_UNASSIGNED ? 'Unassigned' : users.find((u) => u.id === key)?.name ?? 'Unknown'
      const limit = typeof input.limit === 'number' ? input.limit : 15
      return {
        groupBy,
        rows: rows.slice(0, limit).map((r) => ({
          [groupBy]: label(r.key),
          inQueue: formatDuration(r.activeMs),
          total: formatDuration(r.totalMs),
          byStatus: Object.fromEntries(Object.entries(r.byStatus).map(([k, v]) => [k, formatDuration(v)])),
          tickets: r.ticketCount,
        })),
      }
    },
  },
  {
    name: 'bulk_update_tickets',
    description: 'Change status, priority, category, assignee and/or due date on many tickets at once, e.g. "close every overdue Facilities ticket". Choose the tickets with where (the same conditions as list_tickets), and/or by listing ticket numbers. Writes on confirmation: the Confirm card shows the user exactly which tickets and what changes first. The assignee gets one combined Teams message, not one per ticket.',
    parameters: {
      where: WHERE_PARAM,
      ticketNumbers: { type: 'string', description: 'Optional: comma-separated ticket numbers, e.g. "TICK-00012, TICK-00015"' },
      whereStatus: { type: 'string', description: 'Shorthand for a status condition' },
      wherePriority: { type: 'string', description: 'Shorthand for a priority condition' },
      whereCategory: { type: 'string', description: 'Shorthand for a category condition' },
      whereAssigneeName: { type: 'string', description: 'Shorthand for an assignee condition' },
      setStatus: { type: 'string', description: 'New status' },
      setPriority: { type: 'string', description: 'New priority' },
      setCategory: { type: 'string', description: 'New category' },
      setAssigneeName: { type: 'string', description: 'New assignee (fuzzy-matched), or "unassigned"' },
      setDueDate: { type: 'string', description: 'New due date, YYYY-MM-DD' },
    },
    requiresConfirmation: true,
    preview: async (input) => {
      const plan = await planBulkUpdate(input)
      if ('error' in plan) return { summary: '', blocking: plan.error }
      const { describePatch } = await import('../lib/bulkTicketUpdate')
      const n = plan.targets.length
      return {
        summary: `${describePatch(plan.patch, plan.userName)} on ${n} ticket${n === 1 ? '' : 's'}: ${plan.matched}.`,
        items: plan.targets.map((t) => ticketLine(t, plan.userName)),
        total: n,
      }
    },
    handler: async (input) => {
      const plan = await planBulkUpdate(input)
      if ('error' in plan) return { error: plan.error }
      const { targets, patch, userName } = plan
      const { bulkUpdateTickets, listApprovalsForTickets, listWorkflowRules } = await import('./ticketingService')
      const { runBulkUpdate, describePatch } = await import('../lib/bulkTicketUpdate')
      const { notifyBulkAssigned, notifyBulkNewApprovals } = await import('../lib/teamsNotifications')
      const rules = await listWorkflowRules()

      try {
        const r = await runBulkUpdate(targets, patch, {
          update: bulkUpdateTickets, listApprovals: listApprovalsForTickets, rules, userName,
          notifyAssigned: notifyBulkAssigned, notifyApprovals: notifyBulkNewApprovals,
        })
        for (const t of r.updated) auditTicketingTool('bulk_update_tickets', t.id, { patch })
        const skipped = r.skippedIds.length > 0 ? ` ${r.skippedIds.length} skipped (no permission).` : ''
        return {
          ok: true,
          updated: r.updated.map((t) => t.ticketNumber),
          message: `Updated ${r.updated.length} ticket${r.updated.length === 1 ? '' : 's'}: ${describePatch(patch, userName)}.${skipped}`,
        }
      } catch (e) {
        return { error: e instanceof Error ? e.message : String(e) }
      }
    },
  },
  {
    name: 'list_ticket_categories',
    description: 'The ticket categories admins have set up (Settings > Categories), in display order. Use before creating or recategorizing a ticket. Read-only.',
    parameters: {},
    requiresConfirmation: false,
    handler: async () => ({ categories: await activeCategoryNames() }),
  },
  {
    name: 'add_ticket_task',
    description: 'Add a task (checklist item) to a ticket, optionally assigned to a person with a due date. Identify the ticket by number (e.g. TICK-00042) or id. Writes on confirmation.',
    parameters: {
      ticketNumber: { type: 'string', description: 'The ticket number or id' },
      title: { type: 'string', description: 'What needs doing, e.g. "Image replacement laptop"' },
      assigneeName: { type: 'string', description: 'Optional: who owns this task (fuzzy-matched)' },
      dueDate: { type: 'string', description: 'Optional due date, YYYY-MM-DD' },
    },
    requiresConfirmation: true,
    handler: async (input) => {
      const title = typeof input.title === 'string' ? input.title.trim() : ''
      if (!title) return { error: 'The task needs a title.' }
      const dueDate = typeof input.dueDate === 'string' && input.dueDate.trim() ? input.dueDate.trim() : null
      if (dueDate && !/^\d{4}-\d{2}-\d{2}$/.test(dueDate)) return { error: 'Give the due date as YYYY-MM-DD.' }
      const { getTicket, listTickets, listTicketTasks, createTicketTask } = await import('./ticketingService')
      const { getUsers } = await import('@/services/dataService')
      const ref = typeof input.ticketNumber === 'string' ? input.ticketNumber.trim() : ''
      const ticket = (ref ? await getTicket(ref) : null) ?? resolveTicket(ref, await listTickets())
      if (!ticket) return { error: `No ticket matches "${ref}".` }
      let assigneeId: string | null = null
      if (typeof input.assigneeName === 'string' && input.assigneeName.trim()) {
        const u = resolveUser(input.assigneeName, await getUsers())
        if (!u) return { error: `No user matches "${input.assigneeName}".` }
        assigneeId = u.id
      }
      const actor = await currentActor()
      try {
        const existing = await listTicketTasks(ticket.id)
        const seq = existing.reduce((max, t) => Math.max(max, t.seq), 0) + 1
        const task = await createTicketTask(ticket.id, { title, assigneeId, dueDate, seq }, actor.id === 'ai' ? null : actor.id)
        auditTicketingTool('add_ticket_task', ticket.id, { taskId: task.id, title })
        return { ok: true, ticketNumber: ticket.ticketNumber, message: `Added task "${title}" to ${ticket.ticketNumber}.` }
      } catch (e) {
        return { error: e instanceof Error ? e.message : String(e) }
      }
    },
  },
  {
    name: 'complete_ticket_task',
    description: 'Mark a task on a ticket done (or not done). Identify the ticket by number or id and the task by its title (fuzzy-matched) or id from get_ticket. Writes on confirmation.',
    parameters: {
      ticketNumber: { type: 'string', description: 'The ticket number or id' },
      task: { type: 'string', description: 'The task title (or part of it) or its id' },
      done: { type: 'boolean', description: 'true to mark done (default), false to reopen' },
    },
    requiresConfirmation: true,
    handler: async (input) => {
      const { getTicket, listTickets, listTicketTasks, updateTicketTask } = await import('./ticketingService')
      const ref = typeof input.ticketNumber === 'string' ? input.ticketNumber.trim() : ''
      const ticket = (ref ? await getTicket(ref) : null) ?? resolveTicket(ref, await listTickets())
      if (!ticket) return { error: `No ticket matches "${ref}".` }
      const q = typeof input.task === 'string' ? input.task.trim() : ''
      if (!q) return { error: 'Name the task.' }
      const tasks = await listTicketTasks(ticket.id)
      const ql = q.toLowerCase()
      const matches = tasks.filter((t) => t.id === q || t.title.toLowerCase() === ql)
      const candidates = matches.length > 0 ? matches : tasks.filter((t) => t.title.toLowerCase().includes(ql))
      if (candidates.length === 0) return { error: `No task on ${ticket.ticketNumber} matches "${q}".` }
      if (candidates.length > 1) return { error: `Several tasks on ${ticket.ticketNumber} match "${q}": ${candidates.map((t) => `"${t.title}"`).join(', ')}. Be more specific.` }
      const task = candidates[0]!
      const done = input.done !== false
      try {
        await updateTicketTask(task.id, { done })
        auditTicketingTool('complete_ticket_task', ticket.id, { taskId: task.id, done })
        return { ok: true, ticketNumber: ticket.ticketNumber, message: `${done ? 'Completed' : 'Reopened'} "${task.title}" on ${ticket.ticketNumber}.` }
      } catch (e) {
        return { error: e instanceof Error ? e.message : String(e) }
      }
    },
  },
]
